import React, { useState } from 'react';
import axios from 'axios';

import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Heading from '~/components/core/TextComponents/Heading';
import PlusIcon from '~/components/icons/PlusIcon';
import AuthorityLevelsTable from '~/components/SystemConfiguration/DelegatedAuthority/AuthorityLevelsTable';
import { TEXTS } from '~/components/SystemConfiguration/DelegatedAuthority/texts';
import UpsertAuthorityLevelDialog from '~/components/SystemConfiguration/DelegatedAuthority/UpsertAuthorityLevelDialog';
import useDataFetcher from '~/components/useDataFetcher';
import type { AuthorityLevelInfo } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface AuthorityLevelsSectionProps {
  organizationId: number;
  arePaymentLimitsSplit: boolean;
}

const AuthorityLevelsSection: React.FC<AuthorityLevelsSectionProps> = ({ organizationId, arePaymentLimitsSplit }) => {
  const levelsRoute = `/sysconfig/api/v1/organizations/${organizationId}/delegated_authority/levels`;
  const { isLoading, isError, data, reloadData } = useDataFetcher<AuthorityLevelInfo[]>(levelsRoute);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<AuthorityLevelInfo | null>(null);

  const handleAdd = () => {
    setEditingLevel(null);
    setDialogOpen(true);
  };

  const handleEdit = (level: AuthorityLevelInfo) => {
    setEditingLevel(level);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingLevel(null);
  };

  const handleDelete = async (level: AuthorityLevelInfo) => {
    try {
      await axios.delete(`${levelsRoute}/${level.id}`);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <div className="mt-32">
      <div className="mb-12 flex items-center justify-between">
        <Heading variant={Heading.TYPES.H3}>{TEXTS.AUTHORITY_LEVELS_SECTION_TITLE}</Heading>
        <Button color="primary" onClick={handleAdd} startIcon={<PlusIcon iconColor="currentColor" size={12} />}>
          {TEXTS.ADD_LEVEL}
        </Button>
      </div>
      {data && (
        <AuthorityLevelsTable
          levels={data}
          arePaymentLimitsSplit={arePaymentLimitsSplit}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
      {(isLoading || isError) && (
        <SkeletonTable rowsCount={5} columnsCount={arePaymentLimitsSplit ? 8 : 6} isError={isError} />
      )}
      {dialogOpen && (
        <UpsertAuthorityLevelDialog
          organizationId={organizationId}
          arePaymentLimitsSplit={arePaymentLimitsSplit}
          existingLevel={editingLevel}
          onClose={handleDialogClose}
          onSaved={async () => {
            await reloadData();
          }}
        />
      )}
    </div>
  );
};

export default AuthorityLevelsSection;
